import React, { useMemo, useState } from 'react';

const SOUTHAM_IDS = new Set(['ARG', 'BRA', 'URU', 'COL', 'ECU', 'PAR']);
const EUROPE_IDS = new Set(['ESP', 'FRA', 'POR', 'ENG', 'NED', 'BEL', 'GER', 'CRO', 'SUI', 'CZE', 'BIH', 'AUT', 'TUR', 'NOR', 'SWE', 'SCO']);

function colorFor(pct) {
  if (pct <= 0) return 'transparent';
  const t = Math.min(1, Math.sqrt(pct)); // Square root helps highlight lower percentages visually
  // Interpolate HSL color in blue-green spectrum
  const hue = 160 + t * 40; // 160 (teal) -> 200 (light blue)
  const saturation = 70 + t * 20; // 70% -> 90%
  const lightness = 10 + t * 25; // 10% -> 35%
  const alpha = 0.2 + t * 0.8;
  return `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
}

const STAGE_COLS = [
  { key: 'r32',   label: '16es' },
  { key: 'r16',   label: '8es' },
  { key: 'qf',    label: 'Quarts' },
  { key: 'sf',    label: 'Demis' },
  { key: 'final', label: 'Finale' },
  { key: 'champ', label: 'Vainqueur' },
];

export function StageMatrix({ result }) {
  const [filter, setFilter] = useState('all');
  const [hoverRow, setHoverRow] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const rows = useMemo(() => {
    const N = result.numSimulations;
    const all = result.teams.map((team, i) => ({
      team,
      idx: i,
      values: {
        r32:   result.stageCounts.r32[i] / N,
        r16:   result.stageCounts.r16[i] / N,
        qf:    result.stageCounts.qf[i] / N,
        sf:    result.stageCounts.sf[i] / N,
        final: result.stageCounts.final[i] / N,
        champ: result.stageCounts.champion[i] / N,
      },
      elo: team.elo,
    }));
    
    let filtered = all;
    if (filter === 'top16') {
      filtered = [...all].sort((a, b) => b.elo - a.elo).slice(0, 16);
    } else if (filter === 'southam') {
      filtered = all.filter((r) => SOUTHAM_IDS.has(r.team.id));
    } else if (filter === 'europe') {
      filtered = all.filter((r) => EUROPE_IDS.has(r.team.id));
    } else if (filter === 'hosts') {
      filtered = all.filter((r) => r.team.is_host);
    } else if (filter === 'underdogs') {
      filtered = [...all].sort((a, b) => a.elo - b.elo).slice(0, 16);
    }
    
    return filtered.sort((a, b) => b.values.champ - a.values.champ);
  }, [result, filter]);

  const filters = [
    { k: 'all',       label: 'Toutes' },
    { k: 'top16',     label: 'Top 16' },
    { k: 'southam',   label: 'Amérique du Sud' },
    { k: 'europe',    label: 'Europe' },
    { k: 'hosts',     label: 'Pays Hôtes' },
    { k: 'underdogs', label: 'Outsiders' },
  ];

  return (
    <div className="card-elev p-5 sm:p-6 mb-8 border border-[#2A4A6B]">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
            Matrice des Qualifications par Tour
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Probabilités cumulées pour chaque équipe d'atteindre chaque étape du tableau final.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                filter === f.k
                  ? 'border-[#00C853] bg-[#00C853]/10 text-white font-bold'
                  : 'border-[#2A4A6B] bg-transparent text-gray-300 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <div className="overflow-x-auto rounded-xl border border-[#2A4A6B]/50 bg-[#1E3A5F]/10">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 font-mono border-b border-[#2A4A6B]">
              <th className="sticky left-0 z-10 bg-[#152A4A] px-4 py-3 text-left">Équipe</th>
              {STAGE_COLS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-center transition-colors ${
                    hoverCol === col.key ? 'text-[#00C853] font-bold' : ''
                  }`}
                  onMouseEnter={() => setHoverCol(col.key)}
                  onMouseLeave={() => setHoverCol(null)}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.team.id}
                className={`border-b border-[#2A4A6B]/30 hover:bg-[#1E3A5F]/20 transition-colors ${
                  hoverRow === row.idx ? 'bg-[#1E3A5F]/20' : ''
                }`}
                onMouseEnter={() => setHoverRow(row.idx)}
                onMouseLeave={() => setHoverRow(null)}
              >
                {/* Sticky Team Header */}
                <td className="sticky left-0 z-10 bg-[#152A4A]/95 backdrop-blur px-4 py-2">
                  <div className="flex items-center gap-2">
                    <img 
                      src={row.team.flag ? (row.team.flag.startsWith('http') ? row.team.flag : `https://flagcdn.com/w80/${row.team.flag.toLowerCase()}.png`) : `https://flagcdn.com/w80/un.png`} 
                      alt="" 
                      className="w-5 h-3.5 object-cover rounded shadow-sm shrink-0" 
                    />
                    <span className="text-xs sm:text-sm font-semibold text-white truncate">
                      {row.team.name_fr || row.team.name_en || row.team.name}
                    </span>
                  </div>
                </td>
                
                {/* Probabilities Cells */}
                {STAGE_COLS.map((col) => {
                  const v = row.values[col.key];
                  const dim = hoverCol !== null && hoverCol !== col.key && hoverRow !== row.idx;
                  
                  return (
                    <td
                      key={col.key}
                      className="px-3 py-2 text-center font-mono text-xs tabular-nums transition-opacity"
                      style={{
                        backgroundColor: colorFor(v),
                        opacity: dim ? 0.4 : 1,
                      }}
                      onMouseEnter={() => setHoverCol(col.key)}
                      onMouseLeave={() => setHoverCol(null)}
                    >
                      <span className={v > 0.05 ? 'text-white font-semibold' : 'text-gray-400'}>
                        {v > 0.0005 ? `${(v * 100).toFixed(1)}%` : '-'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
